import type { VoidFunctionComponent } from 'react';

import { useQuery } from '../../../contexts/RouterContext';
import { getActivityScopeParams, useLegalAgentActivity } from '../../../hooks/api/legalAgent/useLegalAgentActivity';
import Tabs from '../../generic/Tabs';
import {
  LEGAL_AGENT_SCOPE_MAX,
  isLegalAgentQueryFiltered,
  parseLegalAgentQuery,
  serializeLegalAgentQuery,
} from '../legalAgentQuery';
import type { LegalAgentActor, LegalAgentQuery } from '../legalAgentQuery';

import { ActivityFeed } from './ActivityFeed';
import { ActivityFeedFooter } from './ActivityFeedFooter';

import styles from './LegalAgentActivityPage.module.less';

const ACTOR_TABS: Array<{ label: string; actor: LegalAgentActor | null }> = [
  { label: 'All', actor: null },
  { label: 'Agent', actor: 'agent' },
  { label: 'You', actor: 'user' },
];

export const LegalAgentActivityPage: VoidFunctionComponent = () => {
  const [query, setQuery] = useQuery<LegalAgentQuery, Record<string, unknown>>(parseLegalAgentQuery);
  const activity = useLegalAgentActivity(query);
  const entries = activity.data?.pages.flatMap((page) => page.entries) ?? null;
  //  The params function owns the cap; null means the selection is too long to query.
  const isOverScopeMax = getActivityScopeParams(query) === null;

  return (
    <section data-test-id="legal-agent-activity">
      <Tabs marginBottom="medium">
        {ACTOR_TABS.map(({ label, actor }) => (
          <Tabs.Tab
            key={label}
            label={label}
            isActive={query.actor === actor}
            onSelect={() => setQuery(serializeLegalAgentQuery({ ...query, actor }))}
          />
        ))}
      </Tabs>
      {isOverScopeMax ? (
        <output className={styles.notice}>
          {`Activity shows up to ${LEGAL_AGENT_SCOPE_MAX} ${query.scope === 'releaseGroups' ? 'release groups' : 'projects'} at a time. Remove some to see it.`}
        </output>
      ) : (
        <>
          <ActivityFeed
            //  Covers the first load and a retry after a failed first page, which keeps the error status while it runs.
            isLoading={activity.isFetching && entries === null}
            //  A failed next page keeps the loaded rows; the footer reports it.
            isError={activity.isError && !activity.isFetching && entries === null}
            entries={entries}
            isFiltered={isLegalAgentQueryFiltered(query)}
            showScope={!(query.scope === 'projects' && query.projects.length === 1)}
          />
          <ActivityFeedFooter
            hasRows={(entries?.length ?? 0) > 0}
            hasNextPage={activity.hasNextPage}
            isFetching={activity.isFetching}
            isError={activity.isError}
            fetchNextPage={activity.fetchNextPage}
            refetch={activity.refetch}
          />
        </>
      )}
    </section>
  );
};
